import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { ApiError, login as apiLogin } from "../services/api";

/** Chave usada para persistir o token no navegador. */
const TOKEN_STORAGE_KEY = "carona_access_token";

/**
 * Lê o token persistido. Prioriza o localStorage ("lembrar login") e usa o
 * sessionStorage como fallback para sessões que não pediram para ser lembradas.
 */
function readStoredToken(): string | null {
  try {
    return (
      localStorage.getItem(TOKEN_STORAGE_KEY) ??
      sessionStorage.getItem(TOKEN_STORAGE_KEY)
    );
  } catch {
    return null;
  }
}

/** Persiste o token no storage escolhido e limpa o outro para evitar duplicidade. */
function persistToken(token: string, remember: boolean): void {
  try {
    const target = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    target.setItem(TOKEN_STORAGE_KEY, token);
    other.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Sem storage disponível, o token fica apenas em memória.
  }
}

/** Remove o token de ambos os storages. */
function clearStoredToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Ignora falha ao limpar a sessão.
  }
}

interface AuthContextValue {
  isAuthenticated: boolean;
  token: string | null;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(readStoredToken);

  const login = useCallback(async (email: string, password: string, remember = false) => {
    try {
      const response = await apiLogin(email.trim(), password);

      if (!response?.token) {
        throw new Error("Não foi possível entrar. Tente novamente.");
      }

      persistToken(response.token, remember);

      setToken(response.token);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 422 || error.status === 400) {
          throw new Error("E-mail ou senha inválidos.");
        }
        if (error.status === 429) {
          throw new Error("Muitas tentativas. Aguarde um instante e tente novamente.");
        }
        throw new Error("Não foi possível entrar. Tente novamente mais tarde.");
      }
      throw new Error("Não foi possível entrar. Verifique sua conexão.");
    }
  }, []);

  const logout = useCallback(() => {
    clearStoredToken();
    setToken(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: token !== null,
      token,
      login,
      logout,
    }),
    [token, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }

  return context;
}
